import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/pillars", label: "Pillars" },
  { to: "/story", label: "Story" },
  { to: "/gallery", label: "Gallery" },
  { to: "/free-wallpapers", label: "Wallpapers" },
  { to: "/find-your-pillar", label: "Find My Pillar" },
  { to: "/join", label: "Join" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
      <div className="flex items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-brand" className="font-serif text-lg tracking-wide">
          THREADS <span className="italic text-[#D4AF37]">UNBOUND</span>
          <span className="align-top text-[10px] text-white/50">™</span>
        </Link>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.filter((l) => l.to !== "/" && l.to !== "/find-your-pillar").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                  isActive ? "text-[#D4AF37]" : "text-white/60 hover:text-[#D4AF37]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/find-your-pillar"
            data-testid="nav-link-find-my-pillar"
            className="border border-[#D4AF37]/60 px-4 py-2 text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
          >
            Find My Pillar
          </NavLink>
        </nav>
        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="p-1 text-white/80 transition-colors hover:text-[#D4AF37] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-serif text-3xl font-light transition-colors duration-300 ${
                        isActive ? "text-[#D4AF37]" : "text-white/80 hover:text-[#D4AF37]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
